import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import KofiModal from "./KofiModal";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Card, CardContent } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { 
  Users, Plus, Settings, Copy, LogOut, Loader2, Link2, 
  Search, UserPlus, Check, X, Crown, Shield, BarChart3, User, Trash2, ArrowRightLeft, AlertTriangle, Star, Bell, Heart
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const GuildManager = ({ open, onOpenChange, onSelectGuild }) => {
  const { user, guilds, createGuild, joinGuild, leaveGuild, updateGuild, fetchGuilds } = useAuth();
  const [activeTab, setActiveTab] = useState("list");
  const [loading, setLoading] = useState(false);
  
  // Create guild form
  const [newGuildName, setNewGuildName] = useState("");
  const [newGuildWebhook, setNewGuildWebhook] = useState("");
  const [newGuildEmblem, setNewGuildEmblem] = useState(null);
  
  // Join guild form
  const [inviteCode, setInviteCode] = useState("");
  
  // Search guilds
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [applyMessage, setApplyMessage] = useState("");
  
  // Edit guild form
  const [editingGuild, setEditingGuild] = useState(null);
  const [editName, setEditName] = useState("");
  const [editWebhook, setEditWebhook] = useState("");
  const [editEmblem, setEditEmblem] = useState(null);
  
  // Guild details
  const [selectedGuildDetails, setSelectedGuildDetails] = useState(null);
  const [guildMembers, setGuildMembers] = useState([]);
  const [guildApplications, setGuildApplications] = useState([]);
  const [guildStats, setGuildStats] = useState(null);
  const [inviteUsername, setInviteUsername] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUserToInvite, setSelectedUserToInvite] = useState(null);
  const [searchingUsers, setSearchingUsers] = useState(false);
  
  // Invitations received
  const [pendingInvitations, setPendingInvitations] = useState([]);
  
  // Ko-fi Modal
  const [showKofiModal, setShowKofiModal] = useState(false);

  // Handle emblem upload for create
  const handleEmblemUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error("Image too large (max 500KB)");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      if (isEdit) {
        setEditEmblem(event.target.result);
      } else {
        setNewGuildEmblem(event.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Load pending invitations
  const loadPendingInvitations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guilds/invitations/received`);
      setPendingInvitations(response.data.invitations || []);
    } catch (error) {
      console.error("Failed to load invitations", error);
    }
  };

  // Respond to invitation
  const handleInvitationResponse = async (invitationId, accept) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/guilds/invitations/${invitationId}/respond?accept=${accept}`);
      toast.success(accept ? "You joined the guild!" : "Invitation declined");
      loadPendingInvitations();
      fetchGuilds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond");
    } finally {
      setLoading(false);
    }
  };

  // Load invitations when modal opens
  useEffect(() => {
    if (open && user) {
      loadPendingInvitations();
    }
  }, [open, user]);

  // Set main guild
  const handleSetMainGuild = async (guildId) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/user/main-guild`, { guild_id: guildId });
      toast.success("Main guild updated!");
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set main guild");
    } finally {
      setLoading(false);
    }
  };

  // Search guilds to apply
  const handleSearchGuilds = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/guilds/search/${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Apply to guild
  const handleApplyToGuild = async (guildId) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/guilds/apply`, {
        guild_id: guildId,
        message: applyMessage || null
      });
      toast.success("Application submitted!");
      setApplyMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply");
    } finally {
      setLoading(false);
    }
  };

  // Load guild details
  const loadGuildDetails = async (guild) => {
    setSelectedGuildDetails(guild);
    setActiveTab("details");
    
    try {
      const [membersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/guilds/${guild.id}/members`),
        axios.get(`${API_URL}/api/guilds/${guild.id}/stats`)
      ]);
      setGuildMembers(membersRes.data);
      setGuildStats(statsRes.data);
      
      // Load applications if owner/jr
      const userMembership = membersRes.data.find(m => m.user_id === user.id);
      if (userMembership && ["owner", "jr"].includes(userMembership.role)) {
        const appsRes = await axios.get(`${API_URL}/api/guilds/${guild.id}/applications`);
        setGuildApplications(appsRes.data);
      }
    } catch (error) {
      console.error("Failed to load guild details", error);
    }
  };

  // Handle application action
  const handleApplicationAction = async (applicationId, action) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/guilds/${selectedGuildDetails.id}/applications/action`, {
        application_id: applicationId,
        action: action
      });
      toast.success(`Application ${action}ed!`);
      // Reload
      loadGuildDetails(selectedGuildDetails);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process application");
    } finally {
      setLoading(false);
    }
  };

  // Invite user
  const handleInviteUser = async () => {
    if (!selectedUserToInvite) {
      toast.error("Please select a user to invite");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/guilds/${selectedGuildDetails.id}/invite`, {
        username_or_email: selectedUserToInvite.id,
        message: inviteMessage || null
      });
      toast.success(`Invitation sent to ${selectedUserToInvite.username}!`);
      setInviteUsername("");
      setInviteMessage("");
      setSelectedUserToInvite(null);
      setUserSearchResults([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite user");
    } finally {
      setLoading(false);
    }
  };

  // Search users for invite
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/search?q=${encodeURIComponent(query)}`);
      setUserSearchResults(response.data.users || []);
    } catch (error) {
      console.error("Failed to search users", error);
      setUserSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Debounced user search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inviteUsername && !selectedUserToInvite) {
        searchUsers(inviteUsername);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inviteUsername, selectedUserToInvite]);

  // Promote member
  const handlePromoteMember = async (userId, role) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/guilds/${selectedGuildDetails.id}/promote`, {
        user_id: userId,
        role: role
      });
      toast.success("Member role updated!");
      loadGuildDetails(selectedGuildDetails);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setLoading(false);
    }
  };

  // Kick member
  const handleKickMember = async (userId) => {
    if (!window.confirm("Are you sure you want to kick this member?")) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/guilds/${selectedGuildDetails.id}/kick`, {
        user_id: userId,
        role: "member" // not used for kick, just required by model
      });
      toast.success("Member kicked!");
      loadGuildDetails(selectedGuildDetails);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to kick member");
    } finally {
      setLoading(false);
    }
  };

  // Transfer ownership
  const handleTransferOwnership = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to transfer ownership to ${username}? This action cannot be undone.`)) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/guilds/${selectedGuildDetails.id}/transfer`, {
        new_owner_id: userId
      });
      toast.success(`Ownership transferred to ${username}!`);
      await fetchGuilds();
      loadGuildDetails(selectedGuildDetails);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to transfer ownership");
    } finally {
      setLoading(false);
    }
  };

  // Delete guild
  const handleDeleteGuild = async () => {
    if (!window.confirm(`Are you sure you want to delete "${selectedGuildDetails.name}"? This action cannot be undone and all guild data will be lost.`)) return;
    if (!window.confirm("This is your last chance! Type 'DELETE' to confirm deletion.")) return;
    
    const confirmation = window.prompt("Type 'DELETE' to confirm:");
    if (confirmation !== "DELETE") {
      toast.error("Deletion cancelled - confirmation did not match");
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/guilds/${selectedGuildDetails.id}`);
      toast.success(`Guild "${selectedGuildDetails.name}" has been deleted`);
      await fetchGuilds();
      setSelectedGuildDetails(null);
      setActiveTab("list");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete guild");
    } finally {
      setLoading(false);
    }
  };

  // Start editing guild
  const startEditGuild = () => {
    setEditingGuild(selectedGuildDetails);
    setEditName(selectedGuildDetails.name);
    setEditWebhook(selectedGuildDetails.discord_webhook || "");
    setEditEmblem(selectedGuildDetails.emblem || null);
  };

  // Save guild edit
  const handleSaveGuildEdit = async () => {
    if (!editName.trim()) {
      toast.error("Guild name is required");
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/guilds/${editingGuild.id}`, {
        name: editName.trim(),
        discord_webhook: editWebhook.trim() || null,
        emblem: editEmblem
      });
      toast.success("Guild updated!");
      await fetchGuilds();
      // Reload guild details
      const response = await axios.get(`${API_URL}/api/guilds/${editingGuild.id}`);
      setSelectedGuildDetails(response.data);
      setEditingGuild(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update guild");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGuild = async (e) => {
    e.preventDefault();
    if (!newGuildName.trim()) return;
    
    setLoading(true);
    try {
      const guild = await createGuild(
        newGuildName.trim(), 
        newGuildWebhook.trim() || null,
        newGuildEmblem
      );
      toast.success(`Guild "${guild.name}" created!`);
      setNewGuildName("");
      setNewGuildWebhook("");
      setNewGuildEmblem(null);
      setActiveTab("list");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create guild");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGuild = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    
    setLoading(true);
    try {
      const guild = await joinGuild(inviteCode.trim());
      toast.success(`Joined "${guild.name}"!`);
      setInviteCode("");
      setActiveTab("list");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join guild");
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveGuild = async (guildId, guildName) => {
    if (!window.confirm(`Are you sure you want to leave "${guildName}"?`)) return;
    
    setLoading(true);
    try {
      await leaveGuild(guildId);
      toast.success(`Left "${guildName}"`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave guild");
    } finally {
      setLoading(false);
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Invite code copied!");
  };

  const getRoleIcon = (role) => {
    if (role === "owner") return <Crown className="w-4 h-4 text-yellow-400" />;
    if (role === "jr") return <Shield className="w-4 h-4 text-blue-400" />;
    return <User className="w-4 h-4 text-muted-foreground" />;
  };

  const userRole = selectedGuildDetails ? 
    guildMembers.find(m => m.user_id === user?.id)?.role : null;

  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Login Required</DialogTitle>
            <DialogDescription>
              Please login to manage guilds.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="guild-manager-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Guild Manager
          </DialogTitle>
          <DialogDescription>
            Create, join, or manage your guilds
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="list">My Guilds</TabsTrigger>
            <TabsTrigger value="invitations" className="relative">
              Invites
              {pendingInvitations.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {pendingInvitations.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="create">Create</TabsTrigger>
            <TabsTrigger value="join">Join</TabsTrigger>
            <TabsTrigger value="apply">Apply</TabsTrigger>
          </TabsList>

          {/* Guild List */}
          <TabsContent value="list">
            <ScrollArea className="h-[350px] pr-4">
              {guilds.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No guilds yet</p>
                  <p className="text-sm mt-2">Create or join a guild to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {guilds.map((guild) => (
                    <Card key={guild.id} className="bg-secondary/50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {guild.emblem ? (
                              <img src={guild.emblem} alt={guild.name} className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                                <Users className="w-5 h-5 text-primary" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold">{guild.name}</h3>
                                {guild.pending_applications > 0 && (
                                  <span className="relative">
                                    <Bell className="w-4 h-4 text-amber-400 animate-pulse" />
                                    <span className="absolute -top-1 -right-1 bg-amber-500 text-[10px] text-white rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
                                      {guild.pending_applications}
                                    </span>
                                  </span>
                                )}
                                {user.main_guild_id === guild.id ? (
                                  <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Star className="w-3 h-3 fill-amber-400" />
                                    Main
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => handleSetMainGuild(guild.id)}
                                    className="text-xs text-muted-foreground hover:text-amber-400 transition-colors"
                                    title="Set as main guild"
                                  >
                                    <Star className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {guild.member_count} members • {guild.owner_id === user.id ? "Owner" : "Member"}
                                {guild.pending_applications > 0 && (
                                  <span className="ml-2 text-amber-400">• {guild.pending_applications} pending</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyInviteCode(guild.invite_code)}
                              title="Copy invite code"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadGuildDetails(guild)}
                              title="Guild Details"
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => onSelectGuild(guild.id)}
                            >
                              Open
                            </Button>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">Invite:</span>
                          <code className="bg-background px-2 py-1 rounded font-mono">
                            {guild.invite_code}
                          </code>
                          {guild.discord_webhook && (
                            <span className="flex items-center gap-1 text-emerald-400">
                              <Link2 className="w-3 h-3" />
                              Discord
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invitations">
            <ScrollArea className="h-[350px] pr-4">
              {pendingInvitations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No pending invitations</p>
                  <p className="text-sm mt-2">Guild invitations will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingInvitations.map((invitation) => (
                    <Card key={invitation.id} className="bg-secondary/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {invitation.guild_emblem ? (
                            <img 
                              src={invitation.guild_emblem} 
                              alt={invitation.guild_name} 
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                              <Users className="w-6 h-6 text-primary" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="font-semibold">{invitation.guild_name}</h3>
                            <p className="text-xs text-muted-foreground">
                              Invited by {invitation.invited_by}
                            </p>
                            {invitation.message && (
                              <p className="text-sm text-muted-foreground mt-1 italic">
                                "{invitation.message}"
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleInvitationResponse(invitation.id, true)}
                              disabled={loading}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleInvitationResponse(invitation.id, false)}
                              disabled={loading}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Create Guild */}
          <TabsContent value="create">
            <form onSubmit={handleCreateGuild} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="guildName">Guild Name</Label>
                <Input
                  id="guildName"
                  placeholder="My Awesome Guild"
                  value={newGuildName}
                  onChange={(e) => setNewGuildName(e.target.value)}
                  required
                  minLength={3}
                  maxLength={50}
                  data-testid="create-guild-name-input"
                />
              </div>
              
              {/* Guild Emblem */}
              <div className="space-y-2">
                <Label>Guild Emblem (Optional)</Label>
                <div className="flex items-center gap-4">
                  {newGuildEmblem ? (
                    <div className="relative">
                      <img 
                        src={newGuildEmblem} 
                        alt="Guild emblem preview" 
                        className="w-16 h-16 rounded-lg object-cover border border-border"
                      />
                      <button
                        type="button"
                        onClick={() => setNewGuildEmblem(null)}
                        className="absolute -top-2 -right-2 p-1 bg-destructive rounded-full text-destructive-foreground"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
                      <Users className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleEmblemUpload(e, false)}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Max 500KB, PNG/JPG</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="discordWebhook">Discord Webhook (Optional)</Label>
                <Input
                  id="discordWebhook"
                  placeholder="https://discord.com/api/webhooks/..."
                  value={newGuildWebhook}
                  onChange={(e) => setNewGuildWebhook(e.target.value)}
                  data-testid="create-guild-webhook-input"
                />
                <div className="text-xs text-muted-foreground space-y-1 p-3 bg-secondary/50 rounded-lg">
                  <p className="font-medium text-foreground">How to create a Discord Webhook:</p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Open Discord and go to your server</li>
                    <li>Right-click on the channel → Edit Channel</li>
                    <li>Go to Integrations → Webhooks</li>
                    <li>Click "New Webhook" and copy the URL</li>
                    <li>Paste the URL here</li>
                  </ol>
                  <p className="mt-2 text-primary/80">Boss spawn notifications will be sent to this channel.</p>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="create-guild-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Guild
              </Button>
            </form>
          </TabsContent>

          {/* Join Guild */}
          <TabsContent value="join">
            <form onSubmit={handleJoinGuild} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inviteCode">Invite Code</Label>
                <Input
                  id="inviteCode"
                  placeholder="ABCD1234"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  required
                  className="font-mono uppercase"
                  data-testid="join-guild-code-input"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the invite code shared by a guild member
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="join-guild-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                Join Guild
              </Button>
            </form>
          </TabsContent>

          {/* Apply to Guild */}
          <TabsContent value="apply">
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Search guild by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchGuilds()}
                />
                <Button onClick={handleSearchGuilds} disabled={loading}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              
              <ScrollArea className="h-[250px]">
                {searchResults.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Search for guilds to apply</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {searchResults.map((guild) => (
                      <Card key={guild.id} className="bg-secondary/50">
                        <CardContent className="p-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium">{guild.name}</p>
                            <p className="text-xs text-muted-foreground">{guild.member_count} members</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleApplyToGuild(guild.id)}
                            disabled={loading}
                          >
                            Apply
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
              
              <div className="space-y-2">
                <Label>Application Message (Optional)</Label>
                <Input
                  placeholder="Why do you want to join?"
                  value={applyMessage}
                  onChange={(e) => setApplyMessage(e.target.value)}
                />
              </div>
            </div>
          </TabsContent>

          {/* Guild Details */}
          <TabsContent value="details">
            {selectedGuildDetails && (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-4">
                  {/* Guild Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {selectedGuildDetails.emblem ? (
                        <img src={selectedGuildDetails.emblem} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                          <Users className="w-6 h-6 text-primary" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-bold">{selectedGuildDetails.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {guildMembers.length} members
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {["owner", "jr"].includes(userRole) && (
                        <Button variant="outline" size="sm" onClick={startEditGuild}>
                          <Settings className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => setActiveTab("list")}>
                        Back
                      </Button>
                    </div>
                  </div>

                  {/* Edit Guild Settings (shown when editing) */}
                  {editingGuild && (
                    <Card className="bg-amber-500/10 border-amber-500/30">
                      <CardContent className="p-4 space-y-4">
                        <div className="flex items-center gap-2">
                          <Settings className="w-4 h-4 text-amber-400" />
                          <span className="font-semibold text-amber-400">Edit Guild Settings</span>
                        </div>
                        
                        {/* Guild Name */}
                        <div className="space-y-2">
                          <Label>Guild Name</Label>
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Guild name"
                            maxLength={50}
                          />
                        </div>

                        {/* Guild Emblem */}
                        <div className="space-y-2">
                          <Label>Guild Emblem</Label>
                          <div className="flex items-center gap-4">
                            {editEmblem ? (
                              <div className="relative">
                                <img 
                                  src={editEmblem} 
                                  alt="Emblem preview" 
                                  className="w-16 h-16 rounded-lg object-cover border border-border"
                                />
                                <button
                                  type="button"
                                  onClick={() => setEditEmblem(null)}
                                  className="absolute -top-2 -right-2 p-1 bg-destructive rounded-full text-destructive-foreground"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
                                <Users className="w-6 h-6" />
                              </div>
                            )}
                            <div className="flex-1">
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleEmblemUpload(e, true)}
                                className="cursor-pointer"
                              />
                              <p className="text-xs text-muted-foreground mt-1">Max 500KB, PNG/JPG</p>
                            </div>
                          </div>
                        </div>

                        {/* Discord Webhook */}
                        <div className="space-y-2">
                          <Label>Discord Webhook</Label>
                          <Input
                            value={editWebhook}
                            onChange={(e) => setEditWebhook(e.target.value)}
                            placeholder="https://discord.com/api/webhooks/..."
                          />
                          <div className="text-xs text-muted-foreground space-y-1 p-3 bg-background/50 rounded-lg">
                            <p className="font-medium text-foreground">How to create a Discord Webhook:</p>
                            <ol className="list-decimal list-inside space-y-1 ml-2">
                              <li>Open Discord and go to your server</li>
                              <li>Right-click on the channel → Edit Channel</li>
                              <li>Go to Integrations → Webhooks</li>
                              <li>Click "New Webhook" and copy the URL</li>
                              <li>Paste the URL here</li>
                            </ol>
                            <p className="mt-2 text-amber-400/80">Boss spawn notifications will be sent to this channel.</p>
                          </div>
                        </div>

                        {/* Save/Cancel Buttons */}
                        <div className="flex gap-2">
                          <Button onClick={handleSaveGuildEdit} disabled={loading} className="flex-1">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                            Save Changes
                          </Button>
                          <Button variant="outline" onClick={() => setEditingGuild(null)} disabled={loading}>
                            Cancel
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Guild Stats */}
                  {guildStats && (
                    <Card className="bg-secondary/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <BarChart3 className="w-4 h-4" />
                          <span className="font-semibold">Guild Statistics</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold">{guildStats.total_kills}</p>
                            <p className="text-xs text-muted-foreground">Total Kills</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{guildStats.weekly_kills}</p>
                            <p className="text-xs text-muted-foreground">This Week</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{guildStats.monthly_kills}</p>
                            <p className="text-xs text-muted-foreground">This Month</p>
                          </div>
                        </div>
                        {Object.keys(guildStats.kills_by_member).length > 0 && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <p className="text-sm font-medium mb-2">Top Hunters</p>
                            <div className="space-y-1">
                              {Object.entries(guildStats.kills_by_member).slice(0, 5).map(([name, count], idx) => (
                                <div key={name} className="flex justify-between text-sm">
                                  <span className="flex items-center gap-2">
                                    <span className={idx === 0 ? "text-yellow-400" : "text-muted-foreground"}>
                                      #{idx + 1}
                                    </span>
                                    {name}
                                  </span>
                                  <span className="font-mono">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Invite User (owner/jr only) */}
                  {["owner", "jr"].includes(userRole) && (
                    <Card className="bg-secondary/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <UserPlus className="w-4 h-4" />
                          <span className="font-semibold">Invite User</span>
                        </div>
                        <div className="space-y-3">
                          {/* User Search */}
                          <div className="relative">
                            {selectedUserToInvite ? (
                              <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-lg border border-primary/30">
                                {selectedUserToInvite.avatar ? (
                                  <img src={selectedUserToInvite.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                    <User className="w-4 h-4" />
                                  </div>
                                )}
                                <span className="flex-1 font-medium">{selectedUserToInvite.username}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedUserToInvite(null);
                                    setInviteUsername("");
                                    setUserSearchResults([]);
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                  <Input
                                    placeholder="Search username..."
                                    value={inviteUsername}
                                    onChange={(e) => setInviteUsername(e.target.value)}
                                    className="pl-9"
                                  />
                                  {searchingUsers && (
                                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                                  )}
                                </div>
                                {/* Search Results Dropdown */}
                                {userSearchResults.length > 0 && (
                                  <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                                    {userSearchResults.map((u) => (
                                      <button
                                        key={u.id}
                                        onClick={() => {
                                          setSelectedUserToInvite(u);
                                          setInviteUsername(u.username);
                                          setUserSearchResults([]);
                                        }}
                                        className="w-full flex items-center gap-2 p-2 hover:bg-accent transition-colors text-left"
                                      >
                                        {u.avatar ? (
                                          <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                            <User className="w-4 h-4" />
                                          </div>
                                        )}
                                        <span>{u.username}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          
                          {/* Invitation Message */}
                          <Input
                            placeholder="Invitation message (optional)"
                            value={inviteMessage}
                            onChange={(e) => setInviteMessage(e.target.value)}
                          />
                          
                          <Button 
                            onClick={handleInviteUser} 
                            disabled={loading || !selectedUserToInvite} 
                            className="w-full"
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Send Invitation
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Pending Applications (owner/jr only) */}
                  {["owner", "jr"].includes(userRole) && guildApplications.length > 0 && (
                    <Card className="bg-secondary/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="secondary">{guildApplications.length}</Badge>
                          <span className="font-semibold">Pending Applications</span>
                        </div>
                        <div className="space-y-2">
                          {guildApplications.map((app) => (
                            <div key={app.id} className="flex items-center justify-between p-2 bg-background rounded">
                              <div>
                                <p className="font-medium">{app.username}</p>
                                {app.message && (
                                  <p className="text-xs text-muted-foreground">"{app.message}"</p>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleApplicationAction(app.id, "accept")}
                                  disabled={loading}
                                  className="text-emerald-400 hover:text-emerald-300"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleApplicationAction(app.id, "reject")}
                                  disabled={loading}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Members List */}
                  <Card className="bg-secondary/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="w-4 h-4" />
                        <span className="font-semibold">Members</span>
                      </div>
                      <div className="space-y-2">
                        {guildMembers.map((member) => (
                          <div key={member.user_id} className="flex items-center justify-between p-2 bg-background rounded">
                            <div className="flex items-center gap-3">
                              {member.avatar ? (
                                <img src={member.avatar} alt="" className="w-8 h-8 rounded-full" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                  <span className="text-xs font-bold">{member.username.charAt(0).toUpperCase()}</span>
                                </div>
                              )}
                              <div>
                                <p className="font-medium flex items-center gap-2">
                                  {member.username}
                                  {getRoleIcon(member.role)}
                                </p>
                                <p className="text-xs text-muted-foreground capitalize">{member.role}</p>
                              </div>
                            </div>
                            
                            {/* Member Actions (owner only for promote, owner/jr for kick) */}
                            {userRole === "owner" && member.role !== "owner" && (
                              <div className="flex gap-1">
                                {member.role === "member" ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handlePromoteMember(member.user_id, "jr")}
                                    disabled={loading}
                                    title="Promote to Jr"
                                  >
                                    <Shield className="w-4 h-4 text-blue-400" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handlePromoteMember(member.user_id, "member")}
                                    disabled={loading}
                                    title="Demote to Member"
                                  >
                                    <User className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleKickMember(member.user_id)}
                                  disabled={loading}
                                  className="text-red-400 hover:text-red-300"
                                  title="Kick"
                                >
                                  <LogOut className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                            {userRole === "jr" && member.role === "member" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleKickMember(member.user_id)}
                                disabled={loading}
                                className="text-red-400 hover:text-red-300"
                                title="Kick"
                              >
                                <LogOut className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Danger Zone (Owner Only) */}
                  {userRole === "owner" && (
                    <Card className="bg-red-950/20 border-red-900/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          <span className="font-semibold text-red-400">Danger Zone</span>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">Transfer Ownership</p>
                              <p className="text-xs text-muted-foreground">Transfer this guild to another member</p>
                            </div>
                            <select
                              className="bg-background border border-border rounded px-2 py-1 text-sm"
                              onChange={(e) => {
                                const memberId = e.target.value;
                                if (memberId) {
                                  const member = guildMembers.find(m => m.user_id === memberId);
                                  if (member) {
                                    handleTransferOwnership(memberId, member.username);
                                  }
                                }
                                e.target.value = "";
                              }}
                              defaultValue=""
                            >
                              <option value="">Select member...</option>
                              {guildMembers
                                .filter(m => m.user_id !== user.id)
                                .map((member) => (
                                  <option key={member.user_id} value={member.user_id}>
                                    {member.username}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-red-900/50">
                            <div>
                              <p className="text-sm font-medium text-red-400">Delete Guild</p>
                              <p className="text-xs text-muted-foreground">Permanently delete this guild and all data</p>
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={handleDeleteGuild}
                              disabled={loading}
                              className="gap-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Leave Guild (non-owner) */}
                  {userRole && userRole !== "owner" && (
                    <Button
                      variant="outline"
                      className="w-full text-red-400 border-red-400/50 hover:bg-red-400/10"
                      onClick={() => handleLeaveGuild(selectedGuildDetails.id, selectedGuildDetails.name)}
                      disabled={loading}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Leave Guild
                    </Button>
                  )}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Ko-fi Support Section */}
        <div className="mt-4 pt-4 border-t border-border">
          <button 
            onClick={() => setShowKofiModal(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#FF5E5B]/10 hover:bg-[#FF5E5B]/20 border border-[#FF5E5B]/30 rounded-lg transition-colors"
          >
            <Heart className="w-4 h-4 text-[#FF5E5B] fill-[#FF5E5B]" />
            <span className="text-sm font-medium text-[#FF5E5B]">Support on Ko-fi</span>
          </button>
        </div>
      </DialogContent>
      
      {/* Ko-fi Modal */}
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />
    </Dialog>
  );
};

export default GuildManager;
